import React, { useState, useEffect } from 'react';
import { getPlayerColor } from '../utils';
import CustomSelect from './CustomSelect';
import AutocompleteInput from './AutocompleteInput';

const SetupScreen = ({ user, onStartGame, savedPlayers = [], onBack, onHome, onLogout }) => {
    const [numPlayers, setNumPlayers] = useState(6);
    const [playerNames, setPlayerNames] = useState(['', '', '', '', '', '']);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [touchStart, setTouchStart] = useState(null);

    useEffect(() => {
        // Load last used players from local storage
        if (user) {
            const lastPlayers = localStorage.getItem(`lastGamePlayers_${user.uid}`);
            if (lastPlayers) {
                try {
                    const parsed = JSON.parse(lastPlayers);
                    if (Array.isArray(parsed) && parsed.length >= 4 && parsed.length <= 8) {
                        setNumPlayers(parsed.length);
                        setPlayerNames(parsed);
                    }
                } catch (e) {
                    console.error("Failed to load last players", e);
                }
            }
        }
    }, [user]);

    useEffect(() => {
        setPlayerNames(prev => {
            const newPlayerNames = [...prev];
            if (numPlayers > newPlayerNames.length) {
                return newPlayerNames.concat(Array(numPlayers - newPlayerNames.length).fill(''));
            }
            return newPlayerNames.slice(0, numPlayers);
        });
    }, [numPlayers]);

    // Check for duplicate names
    const getDuplicateNames = () => {
        const trimmedNames = playerNames.map(name => name.trim().toLowerCase()).filter(name => name);
        const duplicates = new Set();
        trimmedNames.forEach((name, index) => {
            if (trimmedNames.indexOf(name) !== index) {
                duplicates.add(name);
            }
        });
        return duplicates;
    };

    const hasDuplicates = getDuplicateNames().size > 0;
    const duplicateNames = getDuplicateNames();

    const handleStartGame = () => {
        const names = playerNames.map((name, i) => name.trim() || `Player ${i + 1}`);
        // Save players for next time
        if (user) {
            localStorage.setItem(`lastGamePlayers_${user.uid}`, JSON.stringify(names));
        }
        onStartGame(names);
    };

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newNames = [...playerNames];
        const draggedItem = newNames[draggedIndex];
        newNames.splice(draggedIndex, 1);
        newNames.splice(index, 0, draggedItem);

        setPlayerNames(newNames);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    // Touch event handlers for mobile
    const handleTouchStart = (e, index) => {
        setTouchStart(index);
        setDraggedIndex(index);
    };

    const handleTouchMove = (e, index) => {
        if (touchStart === null) return;

        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        const playerRow = element?.closest('[data-player-index]');

        if (playerRow) {
            const targetIndex = parseInt(playerRow.getAttribute('data-player-index'));
            if (targetIndex !== draggedIndex && targetIndex >= 0 && targetIndex < numPlayers) {
                const newNames = [...playerNames];
                const draggedItem = newNames[draggedIndex];
                newNames.splice(draggedIndex, 1);
                newNames.splice(targetIndex, 0, draggedItem);

                setPlayerNames(newNames);
                setDraggedIndex(targetIndex);
            }
        }
    };

    const handleTouchEnd = () => {
        setTouchStart(null);
        setDraggedIndex(null);
    };

    const playerOptions = [4, 5, 6, 7, 8].map(n => ({
        value: n,
        label: `${n} Players`
    }));

    return (
        <div className="h-screen bg-[#1e2836] font-['Inter'] text-[#F8FAFC] flex flex-col">
            <header className="relative z-20 flex items-center justify-between w-full max-w-md mx-auto p-4">
                <button
                    onClick={onBack}
                    className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                    title="Back"
                >
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight font-['Roboto_Slab'] text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400 flex-1 text-center">
                    SETUP GAME
                </h1>
            </header>

            <main className="flex-grow overflow-y-auto p-4 sm:p-6">
                <div className="w-full max-w-md mx-auto">
                    <CustomSelect
                        label="Number of Players"
                        options={playerOptions}
                        value={numPlayers}
                        onChange={(value) => setNumPlayers(value)}
                        placeholder="Select number of players"
                    />

                    <div className="flex flex-col gap-3 sm:gap-4 mt-4">
                        {Array.from({ length: numPlayers }, (_, i) => (
                            <div
                                key={i}
                                data-player-index={i}
                                className={`flex items-start gap-3 transition-all ${draggedIndex === i ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
                                    }`}
                                // Drag events are now on the handle, not the whole row
                                onDragOver={(e) => handleDragOver(e, i)}
                                onDragEnd={handleDragEnd}
                            >
                                <div className="flex-1">
                                    <label className="block text-sm sm:text-base font-medium text-[#94A3B8] pb-2">
                                        Player {i + 1}
                                    </label>
                                    <div className="relative">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 sm:pl-3.5">
                                            <span
                                                className="h-2.5 w-2.5 rounded-full"
                                                style={{ backgroundColor: getPlayerColor(i) }}
                                            />
                                        </div>
                                        <AutocompleteInput
                                            value={playerNames[i] || ''}
                                            onChange={(value) => {
                                                const newNames = [...playerNames];
                                                newNames[i] = value;
                                                setPlayerNames(newNames);
                                            }}
                                            suggestions={savedPlayers}
                                            placeholder="Enter player name"
                                            colorIndicator={getPlayerColor(i)}
                                            className="w-full rounded-lg bg-[#2d3e50] border-2 border-[#3d4f61] text-[#F8FAFC] h-12 sm:h-14 pl-7 sm:pl-8 pr-3 sm:pr-4 text-sm sm:text-base placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FBBF24] focus:border-[#FBBF24]"
                                        />
                                        {playerNames[i] && duplicateNames.has(playerNames[i].trim().toLowerCase()) && (
                                            <div className="flex items-center gap-1.5 mt-1.5 text-red-400 text-xs">
                                                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                </svg>
                                                <span>This name is already used</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div
                                    className="flex-shrink-0 flex items-center justify-center h-12 sm:h-14 mt-[28px] cursor-grab active:cursor-grabbing p-2"
                                    title="Drag to reorder"
                                    style={{ touchAction: 'none' }}
                                    draggable
                                    onDragStart={() => handleDragStart(i)}
                                    onTouchStart={(e) => handleTouchStart(e, i)}
                                    onTouchMove={(e) => handleTouchMove(e, i)}
                                    onTouchEnd={handleTouchEnd}
                                >
                                    <svg className="w-5 h-5 text-gray-500 hover:text-amber-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                                        <circle cx="5" cy="4" r="1.5" />
                                        <circle cx="10" cy="4" r="1.5" />
                                        <circle cx="5" cy="10" r="1.5" />
                                        <circle cx="10" cy="10" r="1.5" />
                                        <circle cx="5" cy="16" r="1.5" />
                                        <circle cx="10" cy="16" r="1.5" />
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            <footer className="sticky bottom-0 w-full p-4 sm:p-6 bg-[#1e2836]/80 backdrop-blur-sm">
                <div className="w-full max-w-md mx-auto">
                    <button
                        onClick={handleStartGame}
                        disabled={hasDuplicates}
                        className="w-full h-12 sm:h-14 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] text-gray-900 rounded-xl sm:rounded-2xl text-base sm:text-lg font-bold transition-opacity hover:opacity-90 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Start Game
                    </button>
                </div>
            </footer>
        </div >
    );
};

export default SetupScreen;
